/**
 * Collection Page
 *
 * Handles storefront filtering via Shopify's Section Rendering API.
 * Filter state is managed client-side; only the product grid is re-rendered
 * after each filter change.
 */

import { trapFocus } from "./utils";

const selectors = {
    productGrid: "#collection-results",
    filtersForm: "#filters"
};

const collectionItems = document.querySelector(selectors.productGrid);
const filterForm = document.querySelector(selectors.filtersForm);
const filterToggles = document.querySelectorAll('.filters-toggle');
const filtersUpdatedMessage = document.querySelector('.filters__updated-message');
const allCollectionFilter = document.getElementById('all-collection-filter');
const allTypesFilter = document.getElementById('all-types-filter');
const typesPopover = document.getElementById('type-filters');

let releasePanelFocusTrap = null;

// ---------------------------------------------------------------------------
// Event listeners
// ---------------------------------------------------------------------------

// Deselect an active Collection filter by re-clicking it.
// Re-clicking a checked radio doesn't fire 'change', so we track pre-click
// state with pointerdown, then switch to the hidden 'All' radio on click.
let preClickChecked = false;

filterForm.addEventListener('pointerdown', e => {
    const input = e.target.closest('label')?.querySelector('input[type="radio"]');
    if (input) preClickChecked = input.checked;
});

filterForm.addEventListener('click', e => {
    const input = e.target.closest('label')?.querySelector('input[type="radio"]');
    if (!input || input.value === '' || !preClickChecked) return;
    const allRadio = filterForm.querySelector(`input[type="radio"][name="${CSS.escape(input.name)}"][value=""]`);
    if (allRadio) {
        allRadio.checked = true;
        allRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }
});

filterForm.addEventListener('change', e => {
    if (!e.target.matches('input[type="radio"]')) return;

    // On mobile, show the active state when both "all" options are not checked
    // On desktop, only show the active state when the All Types option is not checked
    const isDesktop = window.matchMedia('(min-width: 1025px)').matches;
    const haveActiveFilters = !isDesktop
        ? !allCollectionFilter.checked || !allTypesFilter.checked
        : !allTypesFilter.checked;
    filterToggles.forEach(toggle => toggle.classList.toggle('active', haveActiveFilters));

    const params = new URLSearchParams();
    for (const [name, value] of new FormData(filterForm)) {
        if (value !== '') params.append(name, value);
    }
    const url = window.location.pathname + (params.size ? '?' + params.toString() : '');
    getData(url);
});

// Edge case: if you're resize from mobile to desktop, the popover state of the filters persists, but our CSS un-styles it as a popover. 
window.matchMedia('(min-width: 1025px)').addEventListener('change', e => {
    if (e.matches && filterForm.matches(':popover-open')) {
        filterForm.hidePopover();
    }
});

// On desktop, focus should move to the type filters when the popover opens
typesPopover.addEventListener('toggle', (e) => {
    if (e.newState === 'open') {
        typesPopover.removeAttribute('inert');
        if (window.matchMedia('(min-width: 1025px)').matches) {
            releasePanelFocusTrap = trapFocus(typesPopover, true);
        }
    } else {
        releasePanelFocusTrap?.();
        releasePanelFocusTrap = null;
        typesPopover.setAttribute('inert', '');
    }
});

// ---------------------------------------------------------------------------
// Updaters
// ---------------------------------------------------------------------------

/**
 * Replace the product grid with fresh markup from a Section Rendering response.
 * @param {Object} result - Section Rendering API response keyed by section ID
 */
function updateElements(result) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(result.collection, 'text/html');
    const responseHtml = doc.body.firstChild;
    collectionItems.innerHTML = responseHtml.querySelector(selectors.productGrid).innerHTML;
}

/**
 * Push the current filter URL to browser history without reloading.
 * @param {string} url
 */
function updateUrl(url) {
    window.history.pushState({}, "", url);
}

// ---------------------------------------------------------------------------
// Rendering API: https://shopify.dev/docs/api/ajax/section-rendering
// ---------------------------------------------------------------------------
/**
 * Fetch filtered collection results via Section Rendering and update the page.
 * @param {string} url - Collection URL, with or without existing query params
 */
let updatedMessageTimer = null;
async function getData(url) {
    clearTimeout(updatedMessageTimer);
    url = url.includes('?') ? url + "&" : url + "?";
    collectionItems.classList.add('working');
    filterForm.classList.add('working');
    filtersUpdatedMessage.classList.remove('shown');
    try {
        const response = await fetch(url + "sections=collection");
        if (!response.ok) {
            throw new Error(`Response status: ${response.status}`);
        }

        const result = await response.json();
        updateElements(result);
        updateUrl(url);
    } catch (error) {
        console.error(error.message);
    }
    collectionItems.classList.remove('working');
    filterForm.classList.remove('working');
    filtersUpdatedMessage.classList.add('shown');
    updatedMessageTimer = setTimeout(() => {
        filtersUpdatedMessage.classList.remove('shown');
    }, 3000);
}
